/**
 * Certificate Template Types
 */

/** Five types of certification templates supported by backend */
export enum CertificateTemplateType {
  COURSE_COMPLETION = 'COURSE_COMPLETION',
  LEARNING_PATH_COMPLETION = 'LEARNING_PATH_COMPLETION',
  CERTIFICATION_GOLD = 'CERTIFICATION_GOLD',
  CERTIFICATION_SILVER = 'CERTIFICATION_SILVER',
  CERTIFICATION_BRONZE = 'CERTIFICATION_BRONZE'
}

/** Template type labels for UI display */
export const CERTIFICATE_TEMPLATE_TYPE_LABELS: Record<CertificateTemplateType, string> = {
  [CertificateTemplateType.COURSE_COMPLETION]: 'Course Completion',
  [CertificateTemplateType.LEARNING_PATH_COMPLETION]: 'Learning Path Completion',
  [CertificateTemplateType.CERTIFICATION_GOLD]: 'Certification - Gold',
  [CertificateTemplateType.CERTIFICATION_SILVER]: 'Certification - Silver',
  [CertificateTemplateType.CERTIFICATION_BRONZE]: 'Certification - Bronze'
};

/** Template type descriptions for UI */
export const CERTIFICATE_TEMPLATE_TYPE_DESCRIPTIONS: Record<CertificateTemplateType, string> = {
  [CertificateTemplateType.COURSE_COMPLETION]: 'Certificate issued upon completing a course',
  [CertificateTemplateType.LEARNING_PATH_COMPLETION]: 'Certificate issued upon completing a learning path',
  [CertificateTemplateType.CERTIFICATION_GOLD]: 'Gold tier certificate for exceptional certification exam performance',
  [CertificateTemplateType.CERTIFICATION_SILVER]: 'Silver tier certificate for excellent certification exam performance',
  [CertificateTemplateType.CERTIFICATION_BRONZE]: 'Bronze tier certificate for passing certification exam'
};

/** Keys of the 5 built-in templates (key = type). Fixed forever, unlike the default choice or the name. */
export const SYSTEM_TEMPLATE_KEYS: readonly string[] = Object.values(CertificateTemplateType);

/** Built-in template: never deletable. Backend sends is_system; older backends fall back to the key. */
export function isSystemTemplate(template: { template_key?: string; is_system?: boolean }): boolean {
  return template.is_system ?? SYSTEM_TEMPLATE_KEYS.includes(template.template_key ?? '');
}

// ============================================
// API DTOs
// ============================================

export interface CreateCertificateTemplateDto {
  template_key: string;
  template_name: string;
  template_type: CertificateTemplateType;
  html_template: string;
  allowed_variables?: string[];
  is_active?: boolean;
  is_default?: boolean;
  created_by?: string;
}

export interface UpdateCertificateTemplateDto {
  template_name?: string;
  template_type?: CertificateTemplateType;
  html_template?: string;
  allowed_variables?: string[];
  is_active?: boolean;
  is_default?: boolean;
  updated_by?: string;
}

export interface CertificateTemplateDto {
  _id: string;
  template_key: string;
  template_name: string;
  template_type: CertificateTemplateType;
  html_template: string;
  allowed_variables: string[];
  is_active: boolean;
  is_default: boolean;
  is_system?: boolean; // one of the 5 built-in templates; can never be deleted
  is_deleted?: boolean;
  created_by?: string;
  updated_by?: string;
  created_at: string;
  updated_at: string;
  used_by?: CertificateTemplateUsage; // only when listed with include_usage
}

/** How many courses / learning paths / certifications chose this template in Edit Settings (any > 0 locks Delete) */
export interface CertificateTemplateUsage {
  courses: number;
  learning_paths: number;
  certifications: number;
}

export interface CertificateTemplateFilterDto {
  template_type?: CertificateTemplateType;
  is_active?: boolean;
  is_default?: boolean;
  include_deleted?: boolean;
  only_deleted?: boolean; // Trash view
  include_usage?: boolean; // adds used_by to each template
  search?: string;
  page?: number;
  limit?: number;
}

export interface PaginatedCertificateTemplateResponse {
  templates: CertificateTemplateDto[];
  total: number;
  page: number;
  limit: number;
  total_pages: number;
}

export interface ApiResponse<T> {
  status: number;
  success: boolean;
  description: string;
  data: T;
  error?: {
    message: string;
    code: string;
    details?: any;
  };
}

// ============================================
// TEMPLATE VARIABLES
// ============================================

/** Common variables available for all certificate types */
const COMMON_VARIABLES = [
  'holder_name',
  'certificate_number',
  'issued_date',
  'company_name',
  'company_logo_url'
];

/** Type-specific variables (certification tiers share the same set) */
const TYPE_SPECIFIC_VARIABLES: Record<string, string[]> = {
  CERTIFICATION: ['certification_name', 'score', 'percentage', 'expiry_date'],
  COURSE_COMPLETION: ['course_title', 'hours', 'completion_date'],
  LEARNING_PATH_COMPLETION: ['learning_path_title', 'total_courses', 'hours', 'completion_date']
};

/** Get default allowed variables for a certificate type */
export function getDefaultAllowedVariables(templateType: CertificateTemplateType | null): string[] {
  if (!templateType) return [...COMMON_VARIABLES];
  const specificKey = templateType.startsWith('CERTIFICATION_') ? 'CERTIFICATION' : templateType;
  return [...COMMON_VARIABLES, ...(TYPE_SPECIFIC_VARIABLES[specificKey] || [])];
}

/** Pre-computed default allowed variables per template type */
export const DEFAULT_ALLOWED_VARIABLES: Record<CertificateTemplateType, string[]> = {
  [CertificateTemplateType.COURSE_COMPLETION]: getDefaultAllowedVariables(CertificateTemplateType.COURSE_COMPLETION),
  [CertificateTemplateType.LEARNING_PATH_COMPLETION]: getDefaultAllowedVariables(CertificateTemplateType.LEARNING_PATH_COMPLETION),
  [CertificateTemplateType.CERTIFICATION_GOLD]: getDefaultAllowedVariables(CertificateTemplateType.CERTIFICATION_GOLD),
  [CertificateTemplateType.CERTIFICATION_SILVER]: getDefaultAllowedVariables(CertificateTemplateType.CERTIFICATION_SILVER),
  [CertificateTemplateType.CERTIFICATION_BRONZE]: getDefaultAllowedVariables(CertificateTemplateType.CERTIFICATION_BRONZE)
};

/** Sample values for certificate variable preview */
export const VARIABLE_SAMPLE_VALUES: Record<string, string> = {
  holder_name: 'John Doe',
  certificate_number: 'CERT-2024-001234',
  issued_date: 'January 15, 2024',
  company_name: 'SkillZen',
  company_logo_url: '/logo.png',
  verification_url: 'https://verify.skillzen.ai/CERT-2024-001234',
  course_title: 'Introduction to Machine Learning',
  hours: '40',
  completion_date: 'January 15, 2024',
  learning_path_title: 'Full Stack Development Mastery',
  total_courses: '12',
  certification_name: 'Advanced Cloud Computing Professional',
  score: '95',
  percentage: '95',
  expiry_date: 'January 15, 2025'
};

/** Get sample value for a variable key, falls back to title-cased key name */
export function getSampleValue(varKey: string): string {
  return VARIABLE_SAMPLE_VALUES[varKey] || varKey.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
}

/** Generate sample data object from allowed variables */
export function generateSampleData(allowedVariables: string[]): Record<string, string> {
  const sampleData: Record<string, string> = {};
  allowedVariables.forEach(varKey => {
    sampleData[varKey] = getSampleValue(varKey);
  });
  return sampleData;
}

// ============================================
// CANVAS-BASED VISUAL EDITOR TYPES
// ============================================

export enum CanvasElementType {
  TEXT = 'TEXT',
  IMAGE = 'IMAGE',
  SHAPE = 'SHAPE',
  LINE = 'LINE',
  QR_CODE = 'QR_CODE',
  SIGNATURE = 'SIGNATURE',
  VARIABLE = 'VARIABLE'
}

export type TextAlign = 'left' | 'center' | 'right' | 'justify';
export type FontWeight = 'normal' | 'bold' | 'light' | '100' | '200' | '300' | '400' | '500' | '600' | '700' | '800' | '900';
export type FontStyle = 'normal' | 'italic';
export type TextDecoration = 'none' | 'underline' | 'line-through';
export type ShapeType = 'rectangle' | 'circle' | 'ellipse' | 'triangle' | 'star' | 'polygon';
export type LineStyle = 'solid' | 'dashed' | 'dotted';

export interface ElementPosition {
  x: number;
  y: number;
  width: number;
  height: number;
  rotation?: number;
  zIndex?: number;
}

export interface TextStyle {
  fontFamily: string;
  fontSize: number;
  fontWeight: FontWeight;
  fontStyle: FontStyle;
  textDecoration: TextDecoration;
  textAlign: TextAlign;
  lineHeight: number;
  letterSpacing: number;
  color: string;
  backgroundColor?: string;
  padding?: number;
  borderRadius?: number;
}

export interface BorderConfig {
  width: number;
  style: LineStyle;
  color: string;
  radius?: number;
}

export interface ShadowConfig {
  enabled: boolean;
  offsetX: number;
  offsetY: number;
  blur: number;
  spread: number;
  color: string;
}

export interface BaseCanvasElement {
  id: string;
  type: CanvasElementType;
  name: string;
  position: ElementPosition;
  locked?: boolean;
  visible?: boolean;
  opacity?: number;
}

export interface TextCanvasElement extends BaseCanvasElement {
  type: CanvasElementType.TEXT;
  content: string;
  style: TextStyle;
  border?: BorderConfig;
  shadow?: ShadowConfig;
}

export interface VariableCanvasElement extends BaseCanvasElement {
  type: CanvasElementType.VARIABLE;
  variableKey: string;
  placeholder: string;
  style: TextStyle;
  format?: 'text' | 'date' | 'number';
  dateFormat?: string;
}

export interface ImageCanvasElement extends BaseCanvasElement {
  type: CanvasElementType.IMAGE;
  src: string;
  alt?: string;
  fit: 'cover' | 'contain' | 'fill' | 'none';
  border?: BorderConfig;
  shadow?: ShadowConfig;
}

export interface ShapeCanvasElement extends BaseCanvasElement {
  type: CanvasElementType.SHAPE;
  shapeType: ShapeType;
  fill: string;
  border?: BorderConfig;
  shadow?: ShadowConfig;
}

export interface LineCanvasElement extends BaseCanvasElement {
  type: CanvasElementType.LINE;
  strokeWidth: number;
  strokeColor: string;
  strokeStyle: LineStyle;
  startPoint: { x: number; y: number };
  endPoint: { x: number; y: number };
}

export interface QRCodeCanvasElement extends BaseCanvasElement {
  type: CanvasElementType.QR_CODE;
  dataKey: string;
  size: number;
  foregroundColor: string;
  backgroundColor: string;
  errorCorrectionLevel: 'L' | 'M' | 'Q' | 'H';
}

export interface SignatureCanvasElement extends BaseCanvasElement {
  type: CanvasElementType.SIGNATURE;
  signatureType: 'image' | 'text';
  src?: string;
  text?: string;
  style?: TextStyle;
  label?: string;
  showLabel?: boolean;
}

export type CanvasElement =
  | TextCanvasElement
  | VariableCanvasElement
  | ImageCanvasElement
  | ShapeCanvasElement
  | LineCanvasElement
  | QRCodeCanvasElement
  | SignatureCanvasElement;

export interface CanvasBackground {
  type: 'solid' | 'gradient' | 'image';
  color?: string;
  gradient?: {
    type: 'linear' | 'radial';
    angle?: number;
    colors: { color: string; position: number }[];
  };
  image?: {
    src: string;
    fit: 'cover' | 'contain' | 'fill' | 'tile';
    opacity?: number;
  };
}

/** Decorative border config — outer/inner borders and corner decorations from certificate templates */
export interface DecorativeBorderConfig {
  outer?: {
    enabled: boolean;
    width: number;
    style: LineStyle;
    color: string;
    inset: { top: number; left: number; right: number; bottom: number };
  };
  inner?: {
    enabled: boolean;
    width: number;
    style: LineStyle;
    color: string;
    inset: { top: number; left: number; right: number; bottom: number };
  };
  corners?: {
    enabled: boolean;
    color: string;
    size: number;
    width: number;
    inset: { top: number; left: number; right: number; bottom: number };
  };
}

export interface CanvasConfig {
  width: number;
  height: number;
  background: CanvasBackground;
  border?: BorderConfig;
  decorativeBorder?: DecorativeBorderConfig;
  padding?: number;
  orientation: 'landscape' | 'portrait';
}

export interface CanvasLayoutData {
  version: string;
  canvas: CanvasConfig;
  elements: CanvasElement[];
  guides?: {
    horizontal: number[];
    vertical: number[];
  };
  gridEnabled?: boolean;
  snapToGrid?: boolean;
  gridSize?: number;
  _initialBorderJson?: string;
  _initialBackgroundJson?: string;
  _initialSimpleBorderJson?: string;
  _initialElementStyles?: Record<string, any>;
}

// ============================================
// CANVAS DEFAULTS & FACTORIES
// ============================================

export const DEFAULT_CANVAS_CONFIG: CanvasConfig = {
  width: 842,
  height: 595,
  orientation: 'landscape',
  background: {
    type: 'solid',
    color: '#FFFFFF'
  },
  border: {
    width: 2,
    style: 'solid',
    color: '#E5E7EB',
    radius: 0
  },
  padding: 40
};

export const DEFAULT_TEXT_STYLE: TextStyle = {
  fontFamily: 'Georgia, serif',
  fontSize: 16,
  fontWeight: 'normal',
  fontStyle: 'normal',
  textDecoration: 'none',
  textAlign: 'center',
  lineHeight: 1.4,
  letterSpacing: 0,
  color: '#111827'
};

// System fonts only — guaranteed available on all servers (including Puppeteer).
// Google Fonts (Playfair Display, Merriweather, Open Sans, Roboto, Lato, Montserrat,
// Dancing Script, Great Vibes) removed until backend font loading is configured.
export const FONT_FAMILIES = [
  { name: 'Georgia', value: 'Georgia, serif' },
  { name: 'Times New Roman', value: '"Times New Roman", Times, serif' },
  { name: 'Arial', value: 'Arial, sans-serif' },
  { name: 'Helvetica', value: 'Helvetica, Arial, sans-serif' },
  { name: 'Verdana', value: 'Verdana, sans-serif' },
  { name: 'Courier New', value: '"Courier New", monospace' },
  { name: 'Tahoma', value: 'Tahoma, sans-serif' },
  { name: 'Trebuchet MS', value: '"Trebuchet MS", sans-serif' },
  { name: 'Segoe UI', value: '"Segoe UI", Tahoma, sans-serif' }
];

/** Predefined element presets for quick add in canvas builder */
export const ELEMENT_PRESETS = {
  title: {
    type: CanvasElementType.TEXT,
    name: 'Certificate Title',
    content: 'Certificate of Completion',
    style: {
      ...DEFAULT_TEXT_STYLE,
      fontFamily: 'Georgia, serif',
      fontSize: 36,
      fontWeight: 'bold' as FontWeight,
      color: '#111827'
    }
  },
  recipientName: {
    type: CanvasElementType.VARIABLE,
    name: 'Recipient Name',
    variableKey: 'holder_name',
    placeholder: '{{holder_name}}',
    style: {
      ...DEFAULT_TEXT_STYLE,
      fontFamily: 'Georgia, serif',
      fontSize: 28,
      fontWeight: 'bold' as FontWeight,
      color: '#111827'
    }
  },
  subtitle: {
    type: CanvasElementType.TEXT,
    name: 'Subtitle',
    content: 'This is to certify that',
    style: {
      ...DEFAULT_TEXT_STYLE,
      fontSize: 14,
      color: '#6B7280'
    }
  },
  courseName: {
    type: CanvasElementType.VARIABLE,
    name: 'Course/Certification Name',
    variableKey: 'course_title',
    placeholder: '{{course_title}}',
    style: {
      ...DEFAULT_TEXT_STYLE,
      fontSize: 20,
      fontWeight: '600' as FontWeight,
      color: '#111827'
    }
  },
  date: {
    type: CanvasElementType.VARIABLE,
    name: 'Issue Date',
    variableKey: 'issued_date',
    placeholder: '{{issued_date}}',
    format: 'date' as const,
    dateFormat: 'MMMM D, YYYY',
    style: {
      ...DEFAULT_TEXT_STYLE,
      fontSize: 12,
      color: '#6B7280'
    }
  },
  signature: {
    type: CanvasElementType.SIGNATURE,
    name: 'Signature',
    signatureType: 'text' as const,
    text: 'Authorized Signature',
    showLabel: true,
    label: 'Director of Learning',
    style: {
      ...DEFAULT_TEXT_STYLE,
      fontFamily: '"Dancing Script", cursive',
      fontSize: 24,
      color: '#111827'
    }
  },
  qrCode: {
    type: CanvasElementType.QR_CODE,
    name: 'Verification QR',
    dataKey: 'verification_url',
    size: 80,
    foregroundColor: '#000000',
    backgroundColor: '#FFFFFF',
    errorCorrectionLevel: 'M' as const
  },
  decorativeLine: {
    type: CanvasElementType.LINE,
    name: 'Decorative Line',
    strokeWidth: 2,
    strokeColor: '#D4AF37',
    strokeStyle: 'solid' as const,
    startPoint: { x: 0, y: 0 },
    endPoint: { x: 200, y: 0 }
  },
  badge: {
    type: CanvasElementType.SHAPE,
    name: 'Badge',
    shapeType: 'rectangle' as ShapeType,
    fill: '#FFD700',
    border: {
      width: 0,
      style: 'solid' as LineStyle,
      color: 'transparent',
      radius: 4
    }
  }
};

export function generateElementId(): string {
  return `el_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

export function createTextElement(
  content: string,
  position: Partial<ElementPosition>,
  style?: Partial<TextStyle>
): TextCanvasElement {
  return {
    id: generateElementId(),
    type: CanvasElementType.TEXT,
    name: 'Text',
    content,
    position: {
      x: position.x || 100,
      y: position.y || 100,
      width: position.width || 200,
      height: position.height || 40,
      rotation: position.rotation || 0,
      zIndex: position.zIndex || 1
    },
    style: { ...DEFAULT_TEXT_STYLE, ...style },
    locked: false,
    visible: true,
    opacity: 1
  };
}

export function createVariableElement(
  variableKey: string,
  placeholder: string,
  position: Partial<ElementPosition>,
  style?: Partial<TextStyle>
): VariableCanvasElement {
  return {
    id: generateElementId(),
    type: CanvasElementType.VARIABLE,
    name: variableKey.replace(/_/g, ' '),
    variableKey,
    placeholder,
    position: {
      x: position.x || 100,
      y: position.y || 100,
      width: position.width || 200,
      height: position.height || 40,
      rotation: position.rotation || 0,
      zIndex: position.zIndex || 1
    },
    style: { ...DEFAULT_TEXT_STYLE, ...style },
    format: 'text',
    locked: false,
    visible: true,
    opacity: 1
  };
}

export function createImageElement(
  src: string,
  position: Partial<ElementPosition>
): ImageCanvasElement {
  return {
    id: generateElementId(),
    type: CanvasElementType.IMAGE,
    name: 'Image',
    src,
    fit: 'contain',
    position: {
      x: position.x || 100,
      y: position.y || 100,
      width: position.width || 150,
      height: position.height || 100,
      rotation: position.rotation || 0,
      zIndex: position.zIndex || 1
    },
    locked: false,
    visible: true,
    opacity: 1
  };
}

export function createDefaultCertificateLayout(
  templateType: CertificateTemplateType
): CanvasLayoutData {
  const contentVariableKey = templateType === CertificateTemplateType.COURSE_COMPLETION ? 'course_title' :
    templateType === CertificateTemplateType.LEARNING_PATH_COMPLETION ? 'learning_path_title' :
    'certification_name';

  const elements: CanvasElement[] = [
    createTextElement('Certificate of Completion', { x: 221, y: 60, width: 400, height: 50 }, {
      fontSize: 36,
      fontWeight: 'bold',
      fontFamily: 'Georgia, serif'
    }),
    createTextElement('This is to certify that', { x: 271, y: 140, width: 300, height: 30 }, {
      fontSize: 14,
      color: '#6B7280'
    }),
    createVariableElement('holder_name', '{{holder_name}}', { x: 221, y: 180, width: 400, height: 45 }, {
      fontSize: 28,
      fontWeight: 'bold'
    }),
    createTextElement('has successfully completed', { x: 271, y: 235, width: 300, height: 30 }, {
      fontSize: 14,
      color: '#6B7280'
    }),
    createVariableElement(
      contentVariableKey,
      `{{${contentVariableKey}}}`,
      { x: 171, y: 275, width: 500, height: 35 },
      { fontSize: 20, fontWeight: '600' }
    )
  ];

  return {
    version: '1.0.0',
    canvas: DEFAULT_CANVAS_CONFIG,
    elements,
    guides: { horizontal: [], vertical: [] },
    gridEnabled: true,
    snapToGrid: true,
    gridSize: 10
  };
}
